/**
 * Écran « Veille des réserves ».
 *
 * Une seule question, posée exercice par exercice et scénario par scénario :
 * la réserve tient-elle au-dessus du seuil d'alerte, et si elle doit passer en
 * dessous, **quand faut-il avoir décidé** ?
 *
 * Le seuil n'est plus la série figée du classeur : il vaut le plancher de
 * trésorerie augmenté du coût de rupture du salarié le plus cher au
 * 31 décembre de l'exercice, et se réévalue donc chaque année.
 *
 * Constater le franchissement ne suffirait pas. Redresser une trajectoire —
 * renégocier une convention, arbitrer une enveloppe, ouvrir une procédure —
 * prend des mois : la décision doit précéder le franchissement d'au moins deux
 * trimestres. C'est ce que l'écran calcule et affiche.
 */

import { dateFr, entier, euros, eurosSigne, keuros, nombre } from '@scc/core';
import type { Charge } from '../api.js';
import { h, vider } from '../dom.js';
import { TEINTES, graphiqueLignes, legende } from '../graphiques.js';
import { badge, enteteSection, insight, kpi, noteLecture, regleAccent } from '../composants.js';

/** Teinte d'un scénario, stable d'un écran à l'autre. */
function teinteScenario(cle: string, rang: number): string {
  if (cle === 'sc1') return TEINTES.slate;
  if (cle === 'sc2') return TEINTES.green;
  if (cle === 'sc3') return TEINTES.amber;
  if (cle === 'sim') return TEINTES.blue;
  return [TEINTES.rose, '#4FA98D', '#8E8EB0'][rang % 3] ?? TEINTES.rose;
}

export function vueVeille(d: Charge): HTMLElement {
  const v = d.veille;
  const lignes = v.lignes;
  const annees = lignes.map((l) => l.exercice);
  const scenarios = lignes[0]?.cellules.map((c) => ({ cle: c.scenario, libelle: c.libelle })) ?? [];

  // ── En-tête chiffré ────────────────────────────────────────────────────
  const courante = d.seuils.applicables[d.meta.exerciceCourant];
  const finale = lignes[lignes.length - 1];
  const proche = v.plusProche;

  const entete = h(
    'div',
    { classe: 'kpi-grid' },
    kpi('neutral', keuros(courante?.seuil ?? 0), null, `Seuil d’alerte ${d.meta.exerciceCourant}`, {
      sens: 'neutre',
      texte: `${keuros(courante?.plancher ?? 0)} de plancher + ${keuros(courante?.coutRuptureMaximal ?? 0)} de rupture`,
    }),
    kpi('neutral', courante?.salarie ?? '—', null, 'Salarié qui commande le seuil', {
      sens: 'neutre',
      texte: 'le plus coûteux à licencier au 31 décembre',
    }),
    kpi(
      (finale?.nbSousSeuil ?? 0) > 0 ? 'alert' : 'neutral',
      entier(finale?.nbSousSeuil ?? 0),
      `sur ${scenarios.length}`,
      `Scénarios sous le seuil en ${finale?.exercice ?? ''}`,
      { sens: (finale?.nbSousSeuil ?? 0) > 0 ? 'down' : 'neutre', texte: 'à la fin de la période projetée' },
    ),
    kpi(
      v.gravite === 'alerte' ? 'alert' : v.gravite === 'vigilance' ? 'warning' : 'progress',
      proche?.echeanceDecision ? dateFr(proche.echeanceDecision) : '—',
      null,
      'Échéance de décision la plus proche',
      {
        sens: v.gravite === 'nominal' ? 'up' : 'down',
        texte:
          proche?.moisRestants !== null && proche?.moisRestants !== undefined
            ? `${entier(proche.moisRestants)} mois · ${proche.libelle}`
            : 'aucun franchissement projeté',
      },
    ),
  );

  // ── Le message d'ensemble ──────────────────────────────────────────────
  const message = proche
    ? insight(
        v.gravite === 'alerte' ? 'negative' : 'warning',
        v.gravite === 'alerte' ? '⚠' : '◷',
        `**${proche.libelle}** ${proche.extrapole ? 'passerait' : 'passe'} sous le seuil d’alerte en ` +
          `${proche.exerciceCritique ?? ''}` +
          (proche.extrapole
            ? `, non pas sur la période projetée mais en prolongeant son dernier résultat annuel ` +
              `(${eurosSigne(proche.franchissementExtrapole?.resultatProlonge ?? 0)}). `
            : `, où il manquerait ${euros(proche.manque ?? 0)}. `) +
          `Un redressement se décide au moins ${v.preavisMois} mois avant : la dernière date utile est le ` +
          `**${dateFr(proche.echeanceDecision ?? '')}**, dans ${entier(proche.moisRestants ?? 0)} mois.`,
      )
    : insight(
        'positive',
        '✓',
        `Aucun scénario ne passe sous le seuil d’alerte sur la période, ni par prolongation de son dernier ` +
          `résultat. La marge la plus faible est de ` +
          `${euros(Math.min(...lignes.flatMap((l) => l.cellules.map((c) => c.marge))))}.`,
      );

  // ── Le graphique : réserves contre seuil ───────────────────────────────
  const graphique = h(
    'div',
    {},
    graphiqueLignes(
      annees,
      scenarios.map((s, i) => ({
        nom: s.libelle,
        couleur: teinteScenario(s.cle, i),
        valeurs: lignes.map((l) => l.cellules.find((c) => c.scenario === s.cle)?.reserve ?? 0),
        epaisseur: s.cle === 'sc3' ? 3 : 2,
      })),
      {
        titre: 'Réserves de chaque scénario contre le seuil d’alerte',
        enteteAbscisse: 'Exercice',
        unite: 'euros',
        bande: lignes.map((l) => l.seuil),
        nomBande: 'Seuil d’alerte',
        depuisZero: true,
      },
    ),
    legende([
      ...scenarios.map((s, i) => ({ nom: s.libelle, couleur: teinteScenario(s.cle, i) })),
      { nom: 'Seuil d’alerte', couleur: TEINTES.danger },
    ]),
  );

  // ── La matrice exercices × scénarios ───────────────────────────────────
  const matrice = h(
    'div',
    { classe: 'enveloppe-table' },
    h(
      'table',
      { classe: 'table-scenario' },
      h('caption', { classe: 'invisible' }, 'Réserves par exercice et par scénario, confrontées au seuil'),
      h(
        'thead',
        {},
        h(
          'tr',
          {},
          h('th', { scope: 'col' }, 'Exercice'),
          h('th', { scope: 'col' }, 'Seuil d’alerte'),
          ...scenarios.map((s) => h('th', { scope: 'col' }, s.libelle)),
        ),
      ),
      h(
        'tbody',
        {},
        ...lignes.map((l) =>
          h(
            'tr',
            { classe: l.nbSousSeuil > 0 ? 'ligne-alerte' : '' },
            h('th', { scope: 'row' }, String(l.exercice)),
            h('td', {}, euros(l.seuil)),
            ...l.cellules.map((c) =>
              h(
                'td',
                {},
                euros(c.reserve),
                c.sousSeuil
                  ? h('span', { classe: 'badge b-danger', style: null }, ' sous le seuil')
                  : c.serre
                    ? h('span', { classe: 'badge b-amber' }, ' marge serrée')
                    : null,
              ),
            ),
          ),
        ),
        // Marge : c'est elle qu'on lit pour savoir de combien on dispose.
        h(
          'tr',
          { classe: 'groupe' },
          h('th', { colspan: scenarios.length + 2 }, 'Marge au seuil, en euros'),
        ),
        ...lignes.map((l) =>
          h(
            'tr',
            {},
            h('th', { scope: 'row' }, String(l.exercice)),
            h('td', {}, '—'),
            ...l.cellules.map((c) => h('td', {}, eurosSigne(c.marge))),
          ),
        ),
      ),
    ),
  );

  // ── Composition du seuil, exercice par exercice ────────────────────────
  const compositionSeuil = h(
    'table',
    { classe: 'comp-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, 'Composition du seuil d’alerte'),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Exercice'),
        h('th', { scope: 'col' }, 'Plancher de trésorerie'),
        h('th', { scope: 'col' }, 'Coût de rupture le plus élevé'),
        h('th', { scope: 'col' }, 'Salarié concerné'),
        h('th', { scope: 'col' }, 'Seuil appliqué'),
        h('th', { scope: 'col' }, 'Seuil voté au classeur'),
        h('th', { scope: 'col' }, 'Écart'),
      ),
    ),
    h(
      'tbody',
      {},
      ...lignes.map((l) => {
        const vote = d.seuils.votes[l.exercice] ?? 0;
        return h(
          'tr',
          {},
          h('th', { scope: 'row' }, String(l.exercice)),
          h('td', { classe: 'num' }, euros(l.plancher)),
          h('td', { classe: 'num' }, euros(l.coutRuptureMaximal)),
          h('td', {}, l.salarie ?? '—'),
          h('td', { classe: 'num' }, euros(l.seuil)),
          h('td', { classe: 'num' }, vote === 0 ? '—' : euros(vote)),
          h('td', { classe: 'num' }, vote === 0 ? '—' : eurosSigne(l.seuil - vote)),
        );
      }),
    ),
  );

  // ── Échéance par scénario ──────────────────────────────────────────────
  const echeances = h(
    'table',
    { classe: 'comp-table encadre-tableau' },
    h('caption', { classe: 'invisible' }, 'Échéance de décision par scénario'),
    h(
      'thead',
      {},
      h(
        'tr',
        {},
        h('th', { scope: 'col' }, 'Scénario'),
        h('th', { scope: 'col' }, 'Creux de réserve'),
        h('th', { scope: 'col' }, 'Franchissement'),
        h('th', { scope: 'col' }, 'Montant manquant'),
        h('th', { scope: 'col' }, 'Décider avant'),
        h('th', { scope: 'col' }, 'Délai restant'),
        h('th', { scope: 'col' }, 'Évaluation'),
      ),
    ),
    h(
      'tbody',
      {},
      ...v.echeances.map((e) =>
        h(
          'tr',
          { classe: e.gravite === 'alerte' ? 'ligne-alerte' : '' },
          h('th', { scope: 'row' }, e.libelle),
          h(
            'td',
            {},
            e.creux ? `${euros(e.creux.montant)} en ${e.creux.exercice}` : '—',
          ),
          h(
            'td',
            {},
            e.exerciceCritique === null
              ? 'aucun'
              : e.extrapole
                ? `${e.exerciceCritique} (extrapolé)`
                : String(e.exerciceCritique),
          ),
          h('td', { classe: 'num' }, e.manque === null ? '—' : euros(e.manque)),
          h('td', {}, e.echeanceDecision === null ? '—' : dateFr(e.echeanceDecision)),
          h(
            'td',
            { classe: 'num' },
            e.moisRestants === null ? '—' : `${entier(e.moisRestants)} mois`,
          ),
          h(
            'td',
            {},
            badge(
              e.gravite === 'alerte' ? 'alerte' : e.gravite === 'vigilance' ? 'vigilance' : 'favorable',
              e.gravite === 'alerte' ? 'À décider' : e.gravite === 'vigilance' ? 'À surveiller' : 'Tenable',
            ),
          ),
        ),
      ),
    ),
  );

  // ── Un exercice en particulier ─────────────────────────────────────────
  //
  // La matrice répond à « comment évolue chaque scénario » ; ce bloc répond à
  // « que disent-ils tous de 2028 ». C'est la vue qu'on ouvre quand on prépare
  // l'arbitrage d'un exercice donné.
  const zoneExercice = h('div', {});
  let exerciceChoisi = annees[0] ?? d.meta.exerciceCourant;

  const dessinerExercice = (): void => {
    vider(zoneExercice);
    const ligne = lignes.find((l) => l.exercice === exerciceChoisi);
    if (!ligne) return;

    const colonnes = d.trajectoires.map((t) => {
      const a = t.annees.find((x) => x.annee === exerciceChoisi);
      const passage = d.tresorerie.parScenario
        .find((x) => x.cle === t.cle)
        ?.passage.find((p) => p.exercice === exerciceChoisi);
      const pm = d.pointsMorts.find((x) => x.cle === t.cle)?.points.find((p) => p.exercice === exerciceChoisi);
      const cellule = ligne.cellules.find((c) => c.scenario === t.cle);
      return { t, a, passage, pm, cellule };
    });

    const rangee = (
      libelle: string,
      valeur: (c: (typeof colonnes)[number]) => string | Node,
      classe = '',
    ): HTMLElement =>
      h(
        'tr',
        { classe },
        h('th', { scope: 'row' }, libelle),
        ...colonnes.map((c) => h('td', {}, valeur(c))),
      );

    zoneExercice.appendChild(
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-scenario' },
          h('caption', { classe: 'invisible' }, `Comparaison des scénarios sur l'exercice ${exerciceChoisi}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, `Exercice ${exerciceChoisi}`),
              ...colonnes.map((c) => h('th', { scope: 'col' }, c.t.libelle)),
            ),
          ),
          h(
            'tbody',
            {},
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Activité')),
            rangee('Adhérents payeurs', (c) => entier(c.a?.adherents ?? 0)),
            rangee('Produits', (c) => euros(c.a?.produits ?? 0)),
            rangee('Charges', (c) => euros(c.a?.charges ?? 0)),
            rangee('Résultat', (c) => eurosSigne(c.a?.resultat ?? 0), 'total'),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Solidité')),
            rangee('Réserve à la clôture', (c) => euros(c.a?.reserve ?? 0)),
            rangee('Seuil d’alerte', () => euros(ligne.seuil)),
            rangee('Marge au seuil', (c) => eurosSigne(c.cellule?.marge ?? 0)),
            rangee('Multiple du seuil', (c) => nombre(c.cellule?.multiple ?? 0, 2)),
            rangee(
              'Situation',
              (c) =>
                badge(
                  c.cellule?.sousSeuil === true ? 'alerte' : c.cellule?.serre === true ? 'vigilance' : 'favorable',
                  c.cellule?.sousSeuil === true
                    ? 'Sous le seuil'
                    : c.cellule?.serre === true
                      ? 'Marge serrée'
                      : 'Tenable',
                ),
            ),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Trésorerie')),
            rangee('Trésorerie de clôture', (c) => euros(c.passage?.tresorerieCloture ?? 0), 'tresorerie'),
            rangee('Variation de l’exercice', (c) => eurosSigne(c.passage?.variationTresorerie ?? 0)),
            rangee('dont variation du BFR', (c) => eurosSigne(-(c.passage?.variationBfr ?? 0))),
            rangee('dont investissements', (c) => euros(c.passage?.investissements ?? 0)),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Point mort')),
            rangee('Adhérents à l’équilibre', (c) => entier(c.pm?.adherents ?? 0)),
            rangee(
              'Écart à l’effectif retenu',
              (c) => ((c.pm?.ecart ?? 0) > 0 ? `+${entier(c.pm?.ecart ?? 0)}` : entier(c.pm?.ecart ?? 0)),
            ),
          ),
        ),
      ),
    );

    const ecarts = colonnes.map((c) => c.a?.reserve ?? 0);
    zoneExercice.appendChild(
      noteLecture(
        `Sur ${exerciceChoisi}, le seuil d'alerte vaut ${euros(ligne.seuil)} — ${euros(ligne.plancher)} de plancher ` +
          `augmentés de ${euros(ligne.coutRuptureMaximal)} pour ${ligne.salarie ?? 'le salarié le plus cher'}. ` +
          `Les scénarios s'écartent de ${euros(Math.max(...ecarts) - Math.min(...ecarts))} sur la réserve : ` +
          `de ${euros(Math.min(...ecarts))} à ${euros(Math.max(...ecarts))}.`,
      ),
    );
  };

  const selecteurExercice = h(
    'div',
    { classe: 'barre-actions' },
    h('span', { classe: 'titre' }, 'Exercice'),
    ...annees.map((an) =>
      h(
        'button',
        {
          classe: 'btn-action',
          type: 'button',
          'aria-pressed': an === exerciceChoisi ? 'true' : 'false',
          surClick: () => {
            exerciceChoisi = an;
            for (const b of selecteurExercice.querySelectorAll('button')) {
              b.setAttribute('aria-pressed', b.textContent === String(an) ? 'true' : 'false');
            }
            dessinerExercice();
          },
        },
        String(an),
      ),
    ),
  );

  dessinerExercice();

  return h(
    'div',
    { classe: 'page', id: 'page-veille', role: 'region', 'aria-label': 'Veille des réserves' },
    h(
      'section',
      {},
      enteteSection(
        1,
        'Veille des réserves',
        `Chaque exercice, chaque scénario, confrontés au seuil d’alerte`,
      ),
      entete,
      message,
      h(
        'p',
        { classe: 'text-block' },
        `**Ce qu'est le seuil d'alerte.** ${d.seuils.mention} En deçà, le syndicat ne pourrait plus rompre ` +
          `le contrat le plus coûteux tout en conservant son plancher de trésorerie. Le coût de rupture ` +
          `croissant avec l'ancienneté et le salaire, le seuil monte d'année en année : ` +
          `${euros(lignes[0]?.seuil ?? 0)} en ${lignes[0]?.exercice ?? ''}, ` +
          `${euros(finale?.seuil ?? 0)} en ${finale?.exercice ?? ''}.`,
      ),
      regleAccent('Réserves contre seuil'),
      graphique,
      matrice,
      noteLecture(
        `Une marge négative signale un franchissement. Une marge inférieure au quart du seuil est signalée ` +
          `« serrée » : elle ne laisse plus d'amortisseur à un aléa d'exercice.`,
      ),
      regleAccent('Un exercice en particulier'),
      selecteurExercice,
      zoneExercice,
      regleAccent('Quand faut-il décider'),
      echeances,
      noteLecture(
        `L'échéance est fixée ${v.preavisMois} mois avant la clôture de l'exercice de franchissement, soit le ` +
          `30 juin de cet exercice. Un franchissement « extrapolé » sort de la période projetée : il prolonge ` +
          `le dernier résultat annuel du scénario et la dernière progression du seuil, sans autre hypothèse. ` +
          `Les délais sont comptés depuis la date d'arrêté des données, le ${dateFr(v.dateArrete)}.`,
      ),
      regleAccent('Composition du seuil'),
      compositionSeuil,
      insight(
        'warning',
        '⚖',
        `**Écart au seuil voté.** Le classeur retenait ${euros(d.seuils.votes[d.meta.exerciceCourant] ?? 0)} pour ` +
          `${d.meta.exerciceCourant} ; le calcul détaillé donne ${euros(courante?.seuil ?? 0)}, soit ` +
          `${eurosSigne((courante?.seuil ?? 0) - (d.seuils.votes[d.meta.exerciceCourant] ?? 0))}. Le seuil voté ` +
          `paraît sous-provisionné. L'application applique le seuil calculé et affiche l'écart : modifier le ` +
          `seuil voté relève d'une décision du Conseil national, pas du logiciel.`,
      ),
      noteLecture(
        `Rappel de méthode : la réserve est le montant des fonds propres à la clôture, ` +
          `${nombre(d.chainage.multipleSeuil, 2)} fois le seuil pour l'exercice en cours. ` +
          `Elle n'est pas la trésorerie — l'écran « Trésorerie et BFR » porte cette distinction.`,
      ),
    ),
  );
}
